import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionCardMenu,
  buildFileRecordKind,
  useResolvedFileRecordActions,
} from "@/object-actions";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import type { ObjectActionHandlers } from "@/object-actions/types";
import type { FileRecord } from "@cohesive/api-client";
import { WorkspaceFolderCard } from "@cohesive/ui-components";
import { useMemo } from "react";

export interface WorkspaceFolderCardItemProps {
  record: FileRecord;
  childCount: number;
  isSelected: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  /** Imperative callbacks the registry handlers wrap (open/rename/move/delete). */
  handlerDeps: FileRecordActionHandlerDeps;
  /** Create verbs the folder offers (new canvas, …), supplied by the page. */
  extraHandlers?: ObjectActionHandlers;
}

/**
 * Registry-backed `WorkspaceFolderCard`. Owns one `useRailRowContextMenu`
 * controller so the card's kebab and right-click open the same manifest-driven
 * folder menu (Open / Rename / Move / Delete).
 */
export function WorkspaceFolderCardItem({
  record,
  childCount,
  isSelected,
  onClick,
  onDoubleClick,
  handlerDeps,
  extraHandlers,
}: WorkspaceFolderCardItemProps) {
  const controller = useRailRowContextMenu();

  const kind = useMemo(() => buildFileRecordKind(record), [record]);

  const { actions } = useResolvedFileRecordActions({
    record,
    surface: "main-view",
    enabled: controller.open,
    handlerDeps,
    extraHandlers,
  });

  return (
    <WorkspaceFolderCard
      record={record}
      childCount={childCount}
      isSelected={isSelected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={controller.onContextMenu}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={kind}
          name={record.path?.split("/").filter(Boolean).pop() || "Folder"}
        />
      }
    />
  );
}
